import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Wrench } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const getSessionId = () => {
  let id = localStorage.getItem("jcd_chat_session");
  if (!id) {
    id = `sess-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem("jcd_chat_session", id);
  }
  return id;
};

const WELCOME = {
  role: "assistant",
  content: "Kia ora! I'm Sparky, the JCD Customs & Marine helper. Ask me about our services, hours, location, or how to book a job.",
};

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);
  const sessionId = useRef(getSessionId());

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);

    try {
      const res = await fetch(`${BACKEND}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: text }),
      });
      if (!res.ok || !res.body) throw new Error("chat failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const data = line.slice(5).trim();
          if (data === "[DONE]") continue;
          try {
            const { delta } = JSON.parse(data);
            if (delta) {
              setMessages((prev) => {
                const next = [...prev];
                next[next.length - 1] = {
                  role: "assistant",
                  content: next[next.length - 1].content + delta,
                };
                return next;
              });
            }
          } catch (_) {}
        }
      }
    } catch (e) {
      setMessages((prev) => {
        const next = [...prev];
        next[next.length - 1] = {
          role: "assistant",
          content: "Sorry, something went wrong. Please call us on 07 865 9073 or use the enquiry form.",
        };
        return next;
      });
    } finally {
      setStreaming(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {open && (
        <div
          data-testid="chat-window"
          className="fixed bottom-24 right-4 sm:right-6 z-50 w-[calc(100vw-2rem)] sm:w-96 h-[32rem] max-h-[70vh] flex flex-col border border-white/10 bg-[#111] shadow-2xl"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-[#0a0a0a]">
            <div className="flex items-center gap-2">
              <span className="flex items-center justify-center w-8 h-8 bg-ember/15 border border-ember/40">
                <Wrench className="w-4 h-4 text-ember" />
              </span>
              <div>
                <p className="font-display text-lg tracking-wide text-white leading-none">SPARKY</p>
                <p className="text-[10px] text-gray-500 mt-0.5">JCD Customs Helper</p>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid="chat-close-btn"
              className="text-gray-500 hover:text-white transition-colors duration-150"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div ref={scrollRef} data-testid="chat-messages" className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={`chat-msg-${m.role}`}
                className={`max-w-[85%] px-3 py-2 text-sm leading-relaxed ${
                  m.role === "user"
                    ? "ml-auto bg-ember text-black"
                    : "mr-auto bg-white/5 text-gray-200 border border-white/10"
                }`}
              >
                {m.content || (streaming && i === messages.length - 1 ? "…" : "")}
              </div>
            ))}
          </div>

          <div className="border-t border-white/10 p-3 flex items-end gap-2">
            <textarea
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              rows={1}
              placeholder="Ask about servicing, WOF, hours…"
              className="flex-1 resize-none bg-[#0a0a0a] border border-white/10 text-gray-200 text-sm px-3 py-2 focus:outline-none focus:border-ember/50 placeholder:text-gray-600 max-h-24"
            />
            <button
              onClick={send}
              disabled={streaming || !input.trim()}
              data-testid="chat-send-btn"
              className="flex items-center justify-center w-10 h-10 bg-ember text-black hover:bg-ember-hover transition-colors duration-150 disabled:opacity-40"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="chat-toggle-btn"
        aria-label="Open chat"
        className="fixed bottom-4 right-4 sm:right-6 z-50 flex items-center justify-center w-14 h-14 bg-ember text-black shadow-2xl hover:bg-ember-hover transition-colors duration-150"
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>
    </>
  );
};
